/**
 * Video Block Component
 * Custom element for inline media video blocks with hover controls
 */
class VideoBlock extends HTMLElement {
  constructor() {
    super();
    this.video = this.querySelector('video');
    this.controls = this.querySelector('[ref="controls"]');
    this.moreInfoLink = this.querySelector('.media-block__more-info');
    
    if (!this.video || !this.controls) return;
    
    // Set up hover listeners
    this.addEventListener('mouseenter', this.showControls.bind(this));
    this.addEventListener('mouseleave', this.hideControls.bind(this));
    
    // Set up touch support for mobile
    this.addEventListener('touchstart', this.handleTouch.bind(this), { passive: true });
    
    // Prevent touch events on controls from bubbling up
    if (this.controls) {
      this.controls.addEventListener('touchstart', (e) => {
        e.stopPropagation();
      }, { passive: true });
    }
    
    // Prevent touch events on more info link from bubbling up
    if (this.moreInfoLink) {
      this.moreInfoLink.addEventListener('touchstart', (e) => {
        e.stopPropagation();
      }, { passive: true });
    }
    
    // Set up control buttons
    this.querySelectorAll('[data-action]').forEach(btn => {
      btn.addEventListener('click', this.handleControl.bind(this));
    });
    
    // Update control icons based on video state
    this.video.addEventListener('play', () => this.updatePlayIcon(false));
    this.video.addEventListener('pause', () => this.updatePlayIcon(true));
    this.video.addEventListener('volumechange', () => this.updateVolumeIcon());
    
    // Initialize icon states
    this.updatePlayIcon(!this.video.paused);
    this.updateVolumeIcon();
  }
  
  showControls() {
    if (this.controls) {
      this.controls.style.display = 'flex';
    }
    if (this.moreInfoLink) {
      this.moreInfoLink.style.opacity = '1';
      this.moreInfoLink.style.pointerEvents = 'auto';
    }
  }
  
  hideControls() {
    if (this.controls && !this.touchActive) {
      this.controls.style.display = 'none';
    }
    if (this.moreInfoLink && !this.touchActive) {
      this.moreInfoLink.style.opacity = '0';
      this.moreInfoLink.style.pointerEvents = 'none';
    }
  }
  
  handleTouch(e) {
    // On mobile, toggle controls visibility on tap
    this.touchActive = !this.touchActive;
    if (this.touchActive) {
      this.showControls();
      // Hide controls after 3 seconds of inactivity
      clearTimeout(this.hideTimeout);
      this.hideTimeout = setTimeout(() => {
        this.touchActive = false;
        this.hideControls();
      }, 3000);
    } else {
      this.hideControls();
    }
  }
  
  handleControl(e) {
    e.preventDefault();
    e.stopPropagation();
    
    const action = e.currentTarget.dataset.action;
    
    if (action === 'toggle-play') {
      if (this.video.paused) {
        this.video.play();
      } else {
        this.video.pause();
      }
    } else if (action === 'toggle-mute') {
      const willUnmute = this.video.muted;
      this.video.muted = !this.video.muted;
      
      // If unmuting this video, mute all other videos
      if (willUnmute) {
        this.muteOtherVideos();
      }
    }
    
    // Reset hide timeout on mobile
    if (this.touchActive) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = setTimeout(() => {
        this.touchActive = false;
        this.hideControls();
      }, 3000);
    }
  }
  
  muteOtherVideos() {
    // Find all other video-block elements on the page
    const allVideoBlocks = document.querySelectorAll('video-block');
    allVideoBlocks.forEach(block => {
      if (block !== this && block.video) {
        block.video.muted = true;
      }
    });
  }
  
  updatePlayIcon(isPaused) {
    const playIcon = this.querySelector('.media-block__icon--play');
    const pauseIcon = this.querySelector('.media-block__icon--pause');
    
    if (playIcon && pauseIcon) {
      if (isPaused) {
        playIcon.style.display = 'block';
        pauseIcon.style.display = 'none';
      } else {
        playIcon.style.display = 'none';
        pauseIcon.style.display = 'block';
      }
    }
  }
  
  updateVolumeIcon() {
    const volumeOnIcon = this.querySelector('.media-block__icon--volume-on');
    const volumeOffIcon = this.querySelector('.media-block__icon--volume-off');
    
    if (volumeOnIcon && volumeOffIcon) {
      if (this.video.muted) {
        volumeOnIcon.style.display = 'none';
        volumeOffIcon.style.display = 'block';
      } else {
        volumeOnIcon.style.display = 'block';
        volumeOffIcon.style.display = 'none';
      }
    }
  }
  
  disconnectedCallback() {
    // Clean up timeout when element is removed
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
    }
  }
}

customElements.define('video-block', VideoBlock);
